// ===========================================
// Portal Newsletter Detail Page
// ===========================================
// Parent view for reading a single newsletter

import { useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { sanitizeHtml } from '../../utils/sanitize';
import {
  Box,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Divider,
  Alert,
  Stack,
} from '@mui/material';
import { ArrowBack as BackIcon, Share as ShareIcon } from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import SocialShareButtons from '../../components/common/SocialShareButtons';
import { usePortalNewsletter } from '../../hooks/useNewsletter';

// ===========================================
// COMPONENT
// ===========================================

export default function NewsletterDetailPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const { slug } = useParams<{ slug: string }>();
  // The portal newsletter pages are mounted under `/:schoolSlug/parent/…`; the
  // old bare `/portal/newsletters` path was never routed anywhere (2026-09-09).
  const listPath = `/${schoolSlug}/parent/newsletters`;

  const { data: newsletter, isLoading, error } = usePortalNewsletter(slug || '');

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !newsletter) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          Newsletter not found or you don't have permission to view it.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to Newsletters
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={newsletter.title}
        subtitle={`By ${newsletter.author.firstName} ${newsletter.author.lastName}${newsletter.publishedAt ? ` • ${new Date(newsletter.publishedAt).toLocaleDateString()}` : ''}`}
        action={
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(listPath)}
          >
            Back to Newsletters
          </Button>
        }
      />

      {/* Featured image */}
      {newsletter.featuredImage && (
        <Paper
          sx={{
            mb: 3,
            overflow: 'hidden',
            borderRadius: 2,
          }}
        >
          <Box
            component="img"
            src={newsletter.featuredImage}
            alt={newsletter.title}
            sx={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'cover',
            }}
          />
        </Paper>
      )}

      {/* Excerpt */}
      {newsletter.excerpt && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body1" fontStyle="italic" color="text.secondary">
            {newsletter.excerpt}
          </Typography>
        </Paper>
      )}

      {/* Content */}
      <Paper sx={{ p: 4 }}>
        {newsletter.sections.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No content available.
          </Typography>
        ) : (
          newsletter.sections
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((section, index) => (
              <Box key={section.id} sx={{ mb: 4 }}>
                {section.title && (
                  <Typography variant="h5" gutterBottom sx={{ mt: index > 0 ? 4 : 0 }}>
                    {section.title}
                  </Typography>
                )}
                <Box
                  sx={{
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                    },
                    '& a': {
                      color: 'primary.main',
                    },
                    '& p': {
                      mb: 2,
                    },
                    '& ul, & ol': {
                      pl: 3,
                      mb: 2,
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      mt: 3,
                      mb: 1,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
                />
                {index < newsletter.sections.length - 1 && (
                  <Divider sx={{ mt: 4 }} />
                )}
              </Box>
            ))
        )}
      </Paper>

      {/* Share Section */}
      <Paper sx={{ p: 3, mt: 3, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ShareIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              Share this newsletter:
            </Typography>
          </Stack>
          <SocialShareButtons
            title={newsletter.title}
            description={newsletter.excerpt || `Read "${newsletter.title}" from our music school newsletter.`}
          />
        </Stack>
      </Paper>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to Newsletters
        </Button>
      </Box>
    </Box>
  );
}
